import { lazy, type ReactElement } from "react";
import {
  createBrowserRouter,
  Navigate,
  type RouteObject,
  RouterProvider,
  useLocation,
  useSearchParams
} from "react-router-dom";
import { RootLayout } from "./ui/topNav/RootLayout";

/**
 * Route table for the unified SPA.
 *
 * - `/` → marketing landing (MarketingPage)
 * - `/viewer` → 3D analysis app (ViewerPage)
 * - `/viewer.html` → 301-style client redirect for old bookmarks. The
 *   Cloudflare Pages `_redirects` file handles this server-side too; the
 *   route only fires if a deep-link slips past the edge.
 *
 * Dev-only routes (registered ONLY when `import.meta.env.DEV` is true):
 * - `/components` → internal UI catalogue (`ComponentsShowcasePage`)
 * - `/map` → US map of every minted parcel (`MapPage`)
 * - `/emails` → 1:1 preview of every outbound transactional email,
 *   sidebar + iframe content area (`EmailsPage`). Renders from the same
 *   `shared/email-templates.ts` module the worker sends from, so design
 *   iteration here ships directly to customer inboxes.
 * - `/modals` → 1:1 preview of every modal/overlay surface (payment flow
 *   permutations, lapsed/expired pages, etc), sidebar + iframe content
 *   area (`ModalsPage`). Renders the exact prod components inside an
 *   iframe-loaded `/modals-preview?fixture=<id>` so edits ship straight
 *   to customers.
 * - `/modals-preview` → full-viewport, dev-only host for ONE modal
 *   fixture, loaded inside the `/modals` iframe (`ModalsPreviewPage`).
 *   Not meant for direct browsing.
 *
 * The dev-only routes are gated behind a build-time constant so Vite
 * tree-shakes both the route registrations AND the dynamic `import(...)`
 * expressions out of production bundles. The corresponding JS chunks are
 * never emitted by Rollup, never referenced from the entry, and never
 * reachable from a deployed Pages build. The `_redirects` file also
 * 302's both paths to `/` at the edge as a belt-and-suspenders defense.
 *
 * Heavier routes stay `React.lazy` so marketing's first paint avoids the
 * Three.js bundle, the showcase-only UI, and the Mapbox layer the map
 * dashboard needs.
 */

const MarketingPage = lazy(() => import("./pages/MarketingPage"));
const ViewerPage = lazy(() => import("./pages/ViewerPage"));
const RecoverPage = lazy(() => import("./pages/RecoverPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const AuthVerifyPage = lazy(() => import("./pages/AuthVerifyPage"));
const TeamAcceptPage = lazy(() => import("./pages/TeamAcceptPage"));
const TeamManagePage = lazy(() => import("./pages/TeamManagePage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const MethodologyPage = lazy(() => import("./pages/MethodologyPage"));
const CompareBuildabilityPage = lazy(
  () => import("./pages/CompareBuildabilityPage")
);
const CompareLotsPage = lazy(() => import("./pages/CompareLotsPage"));
const PropertiesPage = lazy(() => import("./pages/PropertiesPage"));
const B2GPage = lazy(() => import("./pages/B2GPage"));

function ViewerHtmlRedirect(): ReactElement {
  const { search, hash } = useLocation();
  return <Navigate to={`/viewer${search}${hash}`} replace />;
}

/**
 * Wraps `<ViewerPage />` so it remounts whenever the boot inputs in the
 * URL change. React Router otherwise reuses the same component instance
 * across query-string changes when the path matches, and ViewerPage
 * snapshots its gated/full decision + boot inputs at mount time.
 *
 * The key is composed from the params that actually feed `bootSite()`:
 *   - `report` (paid token) — full viewer at the report's address
 *   - `address` / `lat,lon` — gated preview at the typed address
 *   - "" — bare /viewer, default site (Crow Hill)
 *
 * Stripe's redirect-back params (`payment_intent`, `redirect_status`)
 * are intentionally excluded so a successful Stripe return doesn't
 * flicker the scene.
 *
 * `history.replaceState` (used by the in-place ungate after payment)
 * doesn't notify React Router, so the URL flip there does NOT trigger a
 * remount — that's the whole point of the in-place flow. This key only
 * fires on real navigations (Link clicks, navigate() calls).
 */
function ViewerRouteElement(): ReactElement {
  const [params] = useSearchParams();
  const report = params.get("report")?.trim();
  const address = params.get("address")?.trim();
  const lat = params.get("lat")?.trim();
  const lon = params.get("lon")?.trim();
  let key: string;
  if (report) key = `report:${report}`;
  else if (address || lat || lon) key = `site:${address ?? ""}|${lat ?? ""}|${lon ?? ""}`;
  else key = "default";
  return <ViewerPage key={key} />;
}

// Vite's BASE_URL is the value of `base` from vite.config.ts — "/" for the
// Cloudflare Pages deploy, "/17CrowHillLn/" for GH Pages. React Router needs
// the trailing slash stripped, and treats "/" as "no basename".
const rawBase = import.meta.env.BASE_URL || "/";
const basename = rawBase.replace(/\/$/, "");

// Dev-only routes. Wrapping the entire block (including the lazy
// imports) in an `if (import.meta.env.DEV)` branch lets Vite/Rollup
// dead-code-eliminate the dynamic `import()` calls in production. Without
// that, the lazy chunks would be emitted even if the route entries were
// stripped, and the internal source would ship to Pages.
const devOnlyChildren: RouteObject[] = [];
if (import.meta.env.DEV) {
  const ComponentsShowcasePage = lazy(
    () => import("./pages/components/ComponentsShowcasePage")
  );
  const MapPage = lazy(() => import("./pages/map/MapPage"));
  const EmailsPage = lazy(() => import("./pages/emails/EmailsPage"));
  const ModalsPage = lazy(() => import("./pages/modals/ModalsPage"));
  const ModalsPreviewPage = lazy(
    () => import("./pages/modals/ModalsPreviewPage")
  );
  devOnlyChildren.push(
    { path: "/components", element: <ComponentsShowcasePage /> },
    { path: "/map", element: <MapPage /> },
    { path: "/emails", element: <EmailsPage /> },
    { path: "/modals", element: <ModalsPage /> },
    { path: "/modals-preview", element: <ModalsPreviewPage /> }
  );
}

const router = createBrowserRouter(
  [
    {
      element: <RootLayout />,
      children: [
        { path: "/", element: <MarketingPage /> },
        ...devOnlyChildren,
        { path: "/viewer", element: <ViewerRouteElement /> },
        { path: "/viewer.html", element: <ViewerHtmlRedirect /> },
        { path: "/recover", element: <RecoverPage /> },
        { path: "/login", element: <LoginPage /> },
        { path: "/auth/verify", element: <AuthVerifyPage /> },
        { path: "/team/accept", element: <TeamAcceptPage /> },
        { path: "/team", element: <TeamManagePage /> },
        { path: "/dashboard", element: <DashboardPage /> },
        { path: "/checkout", element: <CheckoutPage /> },
        { path: "/methodology", element: <MethodologyPage /> },
        {
          path: "/compare/buildability",
          element: <CompareBuildabilityPage />
        },
        { path: "/compare/lots", element: <CompareLotsPage /> },
        { path: "/properties", element: <PropertiesPage /> },
        { path: "/cities", element: <B2GPage /> },
        { path: "/b2g", element: <B2GPage /> }
      ]
    }
  ],
  basename ? { basename } : undefined
);

export function AppRouter(): ReactElement {
  return <RouterProvider router={router} />;
}
